import { useQuery } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';

import { Icons } from '@/assets/icons/icons';
import { YStack } from '@/components/ui';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/Collapsible';
import TextArea from '@/components/ui/TextArea';
import { AssistantFilesViewer } from '@/features/assistant-files/AssistantFilesViewer';
import {
  getRoutineMemoryFileQuery,
  getRoutineMemoryQuery,
  type RoutineMemory,
} from '@/features/routines/routine-memory.query';
import { userStore } from '@/stores/user.store';

const PANEL_HEIGHT_CLASS_NAME = 'h-[12rem]';

type RoutineMemorySectionProps = {
  routineId: number;
};

/** Read-only view of what a routine carries over between its runs */
export function RoutineMemorySection({ routineId }: RoutineMemorySectionProps) {
  return (
    <Collapsible className="group rounded-lg border border-border bg-surface">
      <CollapsibleTrigger className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-surface-hover text-foreground">
          <Icons.memory className="size-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium">Memory</span>
          <span className="block text-xs text-foreground-muted">
            What this routine remembers between runs
          </span>
        </span>
        <Icons.chevronRight className="size-4 shrink-0 text-foreground-muted transition-transform group-data-[state=open]:rotate-90" />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <RoutineMemoryContent routineId={routineId} />
      </CollapsibleContent>
    </Collapsible>
  );
}

const RoutineMemoryContent = observer(function RoutineMemoryContent({
  routineId,
}: RoutineMemorySectionProps) {
  const {
    data: memory,
    isLoading,
    error,
  } = useQuery(getRoutineMemoryQuery(routineId));

  const notesPlaceholder = isLoading
    ? 'Loading notes...'
    : error
      ? 'Could not load notes. Try closing and reopening this section.'
      : 'No notes yet. Littlebird adds notes as this routine runs.';

  return (
    <YStack className="gap-4 border-t border-border px-3 py-3">
      <YStack className="gap-1.5">
        <p className="text-sm font-medium">Assistant Notes</p>
        <p className="text-xs text-foreground-muted">
          Guidance, corrections, and running state that every run of this
          routine sees
        </p>
        <TextArea
          aria-label="Routine Assistant Notes"
          className={`${PANEL_HEIGHT_CLASS_NAME} resize-none text-sm`}
          placeholder={notesPlaceholder}
          value={memory?.assistant_notes ?? ''}
          readOnly
        />
      </YStack>
      {/* The notes root is null until the routine has an agent slug, and then it has no files to show */}
      {userStore.isInternalUser && memory?.notes_root && (
        <RoutineMemoryFiles
          routineId={routineId}
          notesRoot={memory.notes_root}
        />
      )}
    </YStack>
  );
});

type RoutineMemoryFilesProps = RoutineMemorySectionProps & {
  notesRoot: string;
};

/** Internal-only browser for the VFS files that extend the routine's notes */
function RoutineMemoryFiles({ routineId, notesRoot }: RoutineMemoryFilesProps) {
  const filesSource = useMemo(
    () => ({
      rootPath: notesRoot,
      catalogueQuery: {
        ...getRoutineMemoryQuery(routineId),
        select: (routineMemory: RoutineMemory) => routineMemory.files,
      },
      getFileQuery: (path: string | null) =>
        getRoutineMemoryFileQuery(routineId, path),
    }),
    [notesRoot, routineId]
  );

  return (
    <YStack className="gap-1.5">
      <p className="text-sm font-medium">VFS extension of Assistant Notes</p>
      <p className="text-xs text-foreground-muted">
        Larger memory files this routine reads when it needs them
      </p>
      <AssistantFilesViewer
        panelHeightClassName={PANEL_HEIGHT_CLASS_NAME}
        source={filesSource}
      />
    </YStack>
  );
}
